"use client";
import {
  EpisodeModel,
  EpisodeRentalHistoryModel,
  FavoriteStatusModel,
  GachaModel,
  GetNotifications200Response,
  GetRecentlyCompletedEpisodeInSeries200Response,
  LikedEpisodeModel,
  ProductModel,
  SearchResultModel,
  SeriesModel,
  SeriesAccessModel,
  SeriesSummaryModel,
  UserPointHistoriesSummaryModel,
  UserSettingModel,
  ViewedSeriesModel,
  UserMissionItemsModel,
  UserMissionItemModel,
  UserWalletResponseModel,
} from "@api/models";
import useSWR, { KeyedMutator, SWRConfiguration, SWRResponse } from "swr";
import { StripeCustomerExtended } from "./api/checkout/cardInfo/route";
import { READING_SERIES_ID_KEY, STORE_KEYS } from "./constants";
import { logError } from "@lib/logger";

const fetcher = async (url: string) => {
  const res = await fetch(url, {});
  if (!res.ok) {
    const errorStatus = res.status;
    const errorMessage = `An error occurred while fetching the data:${errorStatus},${url}`;
    const error = new Error(errorMessage);
    logError(errorMessage, { status: errorStatus });
    throw error;
  }
  return await res.json();
};

/** ユーザーの認証状態が関係する場合のSWRのオプション */
const AUTH_SWR_CONFIG: SWRConfiguration = {
  revalidateOnFocus: true,
};

export function useSeries(seriesHash: string) {
  const { data, error, isLoading } = useSWR(`/api/series?seriesHash=${seriesHash}`, fetcher);
  const results = data as { series: SeriesModel };
  return { results, error, isLoading };
}

export function useSeriesAccess(seriesHash: string): {
  startEpisodeIndex: number;
  access: SeriesAccessModel;
  error: string;
  isLoading: boolean;
} {
  const { data, error, isLoading } = useSWR(`/api/series/access?seriesHash=${seriesHash}`, fetcher);
  const results = data as { seriesAccess: SeriesAccessModel };

  let startEpisode = 0;
  if (results?.seriesAccess?.episodeAccesses) {
    let idx = results.seriesAccess.episodeAccesses.length;
    while (idx--) {
      if (results.seriesAccess.episodeAccesses[idx].read) {
        startEpisode = idx;
        break;
      }
    }
  }
  return {
    startEpisodeIndex: startEpisode,
    access: results?.seriesAccess,
    error,
    isLoading,
  };
}

export function useSeriesReadEpisode(seriesHash: string, shouldFetch: boolean) {
  const { data, error, isLoading } = useSWR(
    shouldFetch ? `/api/series/${seriesHash}/recentlyCompletedEpisode` : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const result = data as {
    recentlyCompletedEpisode: GetRecentlyCompletedEpisodeInSeries200Response;
  };
  return { result, error, isLoading };
}

export function useUserInfo(shouldFetch: boolean) {
  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? "/api/user/info" : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const userInfo = data as UserSettingModel;
  return { userInfo, error, isLoading, mutate };
}

export function useUserNotification(page: number) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/notifications?page=${page}`,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const notifications = data as { lastPage: number; notifications: GetNotifications200Response };
  /*
  const notifications = {
    lastPage: 3,
    notifications: [
      {
        id: 0,
        message: "example message1",
        url: "/",
        isRead: false,
        updatedOn: 0,
        createdOn: 1,
      },
      {
        id: 0,
        message: "example message2",
        url: "/",
        isRead: false,
        updatedOn: 0,
        createdOn: 1,
      },
      {
        id: 0,
        message: "example message3",
        url: "/",
        isRead: true,
        updatedOn: 0,
        createdOn: 1,
      },
    ],
  };
  */
  const read = (id: number) => {
    fetch(`/api/notifications/${id}`, { method: "POST" }).then(() => {
      mutate();
    });
  };
  const readAll = () => {
    fetch("/api/notifications", { method: "POST" }).then(() => {
      mutate();
    });
  };
  return { notifications, error, isLoading, read, readAll };
}

export function useCardInfo() {
  const { data, error, isLoading, mutate } = useSWR(
    "/api/checkout/cardInfo",
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const cardInfo = data as StripeCustomerExtended;
  return { cardInfo, error, isLoading, mutate };
}

export function useUserPoints() {
  const { data, error, isLoading, mutate } = useSWR("/api/userPoint", fetcher, AUTH_SWR_CONFIG);
  const userPoints = data as UserWalletResponseModel;
  return { userPoints, error, isLoading, mutate };
}

// TODO: パラメータ名はAPI修正後に再度調整
export function usePointHistory(page: number, category?: string) {
  const { data, error, isLoading, mutate } = useSWR(
    // `/api/userPoint/histories?page=${page}&category=${category}`,
    `/api/userPoint/histories?page=${page}`,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { lastPage: number; pointHistories: UserPointHistoriesSummaryModel[] };
  return { results, error, isLoading, mutate };
}

/* mission */
export function useMissions(shouldFetch: boolean, isAchieved?: boolean) {
  const { data, mutate, error, isLoading } = useSWR(
    shouldFetch
      ? `/api/missions${isAchieved === undefined ? "" : "?isAchieved=" + isAchieved}`
      : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { missions: UserMissionItemsModel };
  return { results, error, isLoading, mutate };
}

export function usePoints() {
  const { data, error, isLoading, mutate } = useSWR("/api/products/points", fetcher);
  const results = data as { points: ProductModel[] };
  return { results, error, isLoading, mutate };
}

export function useViewedSeriesHistory(page: number, shouldFetch: boolean) {
  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? `/api/series/viewedHistory?page=${page}` : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { lastPage: number; viewedSeries: ViewedSeriesModel[] };
  return { results, error, isLoading, mutate };
}

export function useFavoriteSeries(page: number) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/series/favorite?page=${page}`,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { lastPage: number; favoriteSeries: SeriesSummaryModel[] };
  return { results, error, isLoading, mutate };
}

export function useEpisodeRentalHistories(page: number) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/episodes/rentalHistories?page=${page}`,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { lastPage: number; episodeRentalHistories: EpisodeRentalHistoryModel[] };
  return { results, error, isLoading, mutate };
}

export function useEpisode(episodeId: string) {
  const { data, error, isLoading, mutate } = useSWR(`/api/episodes/${episodeId}`, fetcher);
  const results = data as { episode: EpisodeModel };
  return { results, error, isLoading, mutate };
}

export function useEpisodeLiked(page: number) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/episodes/liked?page=${page}`,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data as { lastPage: number; likedEpisodes: LikedEpisodeModel[] };
  return { results, error, isLoading, mutate };
}

export function useEpisodeLikedUser(episodeId: string, shouldFetch: boolean) {
  const { data, error, isLoading, mutate } = useSWR(
    shouldFetch ? `/api/episodes/${episodeId}/like` : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const get = (id: string) => {
    fetch(`/api/episodes/${id}/like`, { method: "GET" }).then(() => {
      mutate();
    });
  };
  const post = (id: string) => {
    fetch(`/api/episodes/${id}/like`, { method: "POST" }).then(() => {
      mutate();
    });
  };

  const results = data as { episodeLikes: { likeNumber: number } };
  return { results, error, isLoading, mutate, get, post };
}

export function useSearch(q: string, page: number, size?: number) {
  const { data, error, isLoading, mutate } = useSWR(
    q !== "" ? `/api/search?q=${q}&page=${page}&size=${size}` : null,
    fetcher,
  );
  const results = data as { searchResult: SearchResultModel } | undefined;
  return {
    /** qが空文字の場合はundefined */
    results,
    error,
    isLoading,
    mutate,
  };
}

export function useEpisodes(seriesHash: string, numEpisodes: number, episodesFrom: number) {
  const { data, error, isLoading, mutate } = useSWR(
    `/api/episodes?seriesHash=${seriesHash}&numEpisodes=${numEpisodes}&episodeFrom=${episodesFrom}`,
    fetcher,
  );
  const results = data as { series: SeriesModel };
  return { results, error, isLoading, mutate };
}

type SeriesFavoriteResult = {
  favoriteStatus?: FavoriteStatusModel;
};

export function useSeriesFavorite(seriesHash: string, shouldFetch: boolean) {
  const apiUrl = `/api/series/${seriesHash}/favorite`;
  const { data, mutate, isLoading } = useSWR<SeriesFavoriteResult>(
    shouldFetch ? apiUrl : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const toggle = () => {
    const method = data?.favoriteStatus?.favorite ? "DELETE" : "POST";
    let toggleData;
    return fetch(apiUrl, { method }).then((response) => {
      if (response.status === 200) {
        //TODO: revalidateの再fetchせずに済ませたい
        mutate();
        toggleData = response.json() as Promise<UserMissionItemModel[] | { message: string }>;
        return toggleData;
      }
    });
  };
  return { results: data, mutate, isLoading, toggle };
}

// おすすめの作品
export function useRecommendedSeries(seriesHash: string) {
  //localStorageから最近読んだものを取得する
  let viewedSeries: string | null = null;
  if (typeof window !== "undefined") {
    viewedSeries = window.localStorage.getItem(READING_SERIES_ID_KEY);
  }
  const viewedSeriesArr = viewedSeries ? JSON.parse(viewedSeries) : [];
  const viewedSeriesStr = Array.isArray(viewedSeriesArr) ? viewedSeriesArr.join(",") : "";

  const apiUrl = `/api/recommend/series?seriesHash=${seriesHash}&viewedSeries=${Array(viewedSeriesStr)}`;
  const { data, mutate, isLoading } = useSWR(apiUrl, fetcher, AUTH_SWR_CONFIG);
  const results = data as { recommendedSeries: SeriesSummaryModel[] };
  return { results, mutate, isLoading };
}

/*
ガチャ
*/
export type GachaResult = { gacha: GachaModel };
/**
 * ガチャ状態取得、ガチャを引く
 * @param shouldFetch APIをコールするか。トークンが必須のため、(現状は)ログインユーザのみtrueを入れる。falseの場合はresultsがundefined
 * @returns
 */
export function useGacha(shouldFetch: boolean) {
  const apiUrl = "/api/gacha";
  const { data, mutate, isLoading, error } = useSWR<GachaResult>(
    shouldFetch ? apiUrl : null,
    fetcher,
    AUTH_SWR_CONFIG,
  );
  const results = data;
  const roll = () => {
    mutate(() => {
      return fetch(apiUrl, { method: "POST" })
        .then((res) => res.json())
        .then((res) => {
          return res;
        })
        .catch(() => {
          return results;
        });
    });
  };

  return {
    /** 非ログインユーザー・ロード中の場合はundefined */
    results,
    roll,
    mutate,
    isLoading,
    error,
  };
}

export function useStaticStore<T>(key: STORE_KEYS, initialValue: T | undefined = undefined) {
  /* SWRをstoreとして使う */
  const opt: SWRConfiguration = {};
  if (initialValue) {
    opt.fallbackData = initialValue;
  }
  return useSWR(key, null, opt) as SWRResponse<T>;
}

/**
 * adminModeのON/OFFを取得/設定する
 */
export function useAdminMode() {
  const res: SWRResponse<boolean> = useStaticStore<boolean>(STORE_KEYS.inAdminMode, false);
  return res;
}

export function useReadonlyLocalStorageStore<T>(key: string, initialValue: T) {
  const opt: SWRConfiguration = {};
  if (initialValue) {
    opt.fallbackData = initialValue;
  }
  return useSWR(
    key,
    (key) => {
      return new Promise((resolve) => {
        if (window && window.localStorage) {
          const val = window.localStorage.getItem(key);
          if (val) {
            resolve(val as T);
            return;
          }
        }
        resolve(initialValue);
      });
    },
    opt,
  ) as SWRResponse<T>;
}

/* Toast 関連  */
export type ToastType = "error" | "bonus" | "mission" | undefined;
export type ToastData = {
  message: string;
  died: boolean;
  type: ToastType;
  toastIndex: number;
  bonusValue?: number;
  missionContent?: UserMissionItemModel;
  isStripeError?: boolean;
};
export type PushToast = (
  message: string,
  opt?:
    | {
        livingSec?: number | undefined;
        type: ToastType;
        bonusValue?: number;
        mission?: UserMissionItemModel;
        isStripeError?: boolean;
      }
    | undefined,
) => void;

let toastIndex: number = 0;
let toastData: ToastData[] = [];
let mutateToastData: KeyedMutator<ToastData[]>;
const pushToast: PushToast = (
  message: string,
  opt?: {
    livingSec?: number;
    type: ToastType;
    bonusValue?: number;
    mission?: UserMissionItemModel;
    isStripeError?: boolean;
  },
) => {
  const idx = toastIndex;
  const livingSec = opt?.livingSec ? opt.livingSec : 5;
  const type = opt?.type;
  const newToastItem: ToastData = {
    message,
    toastIndex: idx,
    type,
    died: false,
    bonusValue: opt?.bonusValue,
    missionContent: opt?.mission,
    isStripeError: opt?.isStripeError,
  };

  if (toastData && Array.isArray(toastData)) {
    toastData = [newToastItem, ...toastData];
  } else {
    toastData = [newToastItem];
  }
  mutateToastData(toastData);
  window.setTimeout(() => {
    const newToastArr = toastData?.map((item) => {
      if (item.toastIndex === idx) {
        item.died = true;
      }
      return Object.assign({}, item);
    });
    mutateToastData(newToastArr);
    toastData = newToastArr;
  }, livingSec * 1000);
  window.setTimeout(
    () => {
      const newToastArr = toastData?.filter((item) => {
        return item.toastIndex !== idx;
      });
      mutateToastData(newToastArr);
      toastData = newToastArr;
    },
    livingSec * 1000 + 2000,
  );
  toastIndex++;
};
export function useToast() {
  const { data, mutate } = useSWR<ToastData[]>("toast", null, { fallbackData: [] });
  mutateToastData = mutate;
  return { toastData, pushToast: pushToast };
}

/* bulk購入まわり */

export type CheckedEpisodeDetail = {
  idx: number;
  articleHash: string;
  articleIndexId: number | undefined;
  canUseTicket: boolean;
  price: number;
} | null;

export function useBulkStore(
  seriesHash: string,
  initialValue: CheckedEpisodeDetail[] | undefined = undefined,
) {
  const opt: SWRConfiguration = {};
  if (initialValue) {
    opt.fallbackData = initialValue;
  }
  const { isLoading, mutate, data } = useSWR<CheckedEpisodeDetail[]>(
    "store_bulk_" + seriesHash,
    null,
    opt,
  );
  const add = (article: CheckedEpisodeDetail) => {
    if (article && Array.isArray(data)) {
      const newData = [...data];
      newData[article?.idx] = article;
      mutate(newData);
    }
  };
  const remove = (article: CheckedEpisodeDetail) => {
    if (article && Array.isArray(data)) {
      const newData = [...data];
      newData[article?.idx] = null;
      mutate(newData);
    }
  };

  return { add, remove, data, isLoading, mutate };
}
